import type { ReactNode } from "react";
import { WifiSlash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export interface OfflinePageProps {
  /** Retry handler; default refreshes the window. */
  retry?: () => void;
}

/**
 * Fatal-network fallback rendered at `/offline` and by boundaries that catch
 * `ApiError.kind === "network"` while `navigator.onLine === false`.
 *
 * Spec: docs/architecture/states-pattern.md §2.4 / §6.2.
 */
export function OfflinePage({ retry }: OfflinePageProps = {}): ReactNode {
  const handleRetry = () => {
    if (retry) {
      retry();
      return;
    }
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };
  return (
    <main
      id="main-content"
      tabIndex={-1}
      role="main"
      className="flex min-h-screen w-full flex-col items-center justify-center gap-6 bg-background p-8 text-center"
    >
      <div
        aria-hidden="true"
        className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/15 text-destructive"
      >
        <WifiSlash className="h-8 w-8" weight="bold" />
      </div>
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold text-foreground">Você está offline</h1>
        <p className="mx-auto max-w-md text-sm text-muted-foreground">
          Não conseguimos alcançar o servidor. Verifique sua conexão e tente novamente.
        </p>
      </div>
      <Button type="button" onClick={handleRetry}>
        Tentar novamente
      </Button>
    </main>
  );
}
